import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import qs from "query-string";
import cn from "clsx";
import { Trans, useTranslation } from "react-i18next";

import { CommonService } from "@/services";
import { ButtonWithLoader } from "@/components/common/buttons";
import ErrorLayout from "@/components/errorLayout";
import { hideErrorPage, showErrorPage } from "@/redux/actions/common.action";
import { useAppDispatch } from "@/redux/store";
import { useRouteSlug } from "@/hooks";

import "./style.scss";

type RequestStatus = "idle" | "success" | "error";
type SupportedLanguage = "en" | "ar";

const getValidLang = (lang?: string): SupportedLanguage =>
  lang === "ar" ? "ar" : "en";

const getCustomerUid = (query: Record<string, unknown>): string => {
  const value = query.customer_uid;

  if (Array.isArray(value)) return String(value[0] || "");
  if (typeof value === "string") return value;

  return "";
};

const UnsubscribePage = () => {
  const { t, i18n } = useTranslation("unsubscribe");
  const dispatch = useAppDispatch();
  const { search, pathname } = useLocation();
  const routeSlug = useRouteSlug();
  const parsedQuery = useMemo(
    () => qs.parse(search) as Record<string, unknown>,
    [search]
  );
  const queryLang = Array.isArray(parsedQuery.lang)
    ? String(parsedQuery.lang[0] || "")
    : String(parsedQuery.lang || "");
  const pathLang =
    pathname.startsWith("/ar-") || pathname.startsWith("/ar/") ? "ar" : "en";
  const language = getValidLang(queryLang || pathLang || "en");
  const requestLang: SupportedLanguage = language || "en";
  const customerUid = getCustomerUid(parsedQuery);
  const hasRouteSlugInPath = /^\/[a-z]{2}-[a-z]{2}(\/|$)/i.test(pathname);
  const defaultRouteSlug = language === "ar" ? "ar-ae" : "en-ae";
  const homeRoute = `/${hasRouteSlugInPath ? routeSlug : defaultRouteSlug}/`;

  const [status, setStatus] = useState<RequestStatus>("idle");
  const [isLoading, setIsLoading] = useState(false);

  const isRTL = language === "ar";

  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [i18n, language]);

  useEffect(() => {
    if (!customerUid) {
      dispatch(hideErrorPage());
      dispatch(showErrorPage("is404", true));
    }

    return () => {
      dispatch(hideErrorPage());
    };
  }, [customerUid, dispatch]);

  const onUnsubscribe = async () => {
    if (isLoading) return;

    if (!customerUid) {
      setStatus("error");
      return;
    }

    setIsLoading(true);

    try {
      const response = await CommonService.unsubscribeEmail({
        customer_uid: customerUid,
        lang: requestLang
      });

      setStatus(response?.success ? "success" : "error");
    } catch (error) {
      setStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  if (!customerUid)
    return (
      <ErrorLayout
        redirectTo={homeRoute}
        buttonLabel={t("home")}
        forceFullPageRedirect
      />
    );

  return (
    <div
      className={cn("unsubscribe-page", { rtl: isRTL })}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div
        className={cn("unsubscribe-card", {
          "success-state": status === "success"
        })}
      >
        {status === "success" ? (
          <>
            <p className={cn("unsubscribe-result", "success")}>
              {t("success")}
            </p>
            <button
              type="button"
              className="unsubscribe-button"
              onClick={() => (window.location.href = homeRoute)}
            >
              {t("home")}
            </button>
          </>
        ) : (
          <>
            <p className="unsubscribe-intro">
              <Trans
                i18nKey="intro"
                ns="unsubscribe"
                components={{
                  bold: <strong />
                }}
              />
            </p>

            <ButtonWithLoader
              type="button"
              className="unsubscribe-button"
              showLoader={isLoading}
              disabled={isLoading}
              aria-busy={isLoading}
              onClick={onUnsubscribe}
            >
              {t("button")}
            </ButtonWithLoader>

            {status === "error" && (
              <p className="unsubscribe-result error">{t("error")}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UnsubscribePage;
